import { importsPreview, useImportsFormats, useImportsLineTables, useImportsStart, useImportsUpload } from "@api/generated/imports/imports";
import { useMeasurementsTables } from "@api/generated/measurements/measurements";
import type { ImportIn, ImportMappingInput, ImportMappingOutput, UploadInfo } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { usePreview } from "@app/preview";
import { hasRole, useSaveSetting, useSeasonFromLink, useSettings } from "@app/workspace";
import {
  CheckPanel,
  ColumnTable,
  FileCard,
  FormatList,
  loadable,
  loadableLines,
  MappingControls,
  type Policy,
} from "@features/trials/ImportParts";
import "@features/trials/import.css";
import { useWatchedJob, WatchedJobProgress } from "@features/system/jobs";
import { Button, Checkbox, FileButton, Loader } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DropZone } from "@ui/DropZone";
import { formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { Steps } from "@ui/Steps";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const Route = createFileRoute("/trials/import")({
  validateSearch: z.object({ ev: z.number().int().optional() }),
  component: ImportPage,
});

const STEPS = ["file", "format", "check", "load"] as const;
const NO_MAPPING: ImportMappingInput = { table: "", columns: {}, header_row: 1, code: null };
/** A mapping as it is remembered (every field set; the row filter belongs to one file, it is not kept). */
const asTemplate = (m: ImportMappingInput): ImportMappingOutput => ({
  table: m.table ?? "",
  code: m.code ?? null,
  th: m.th ?? null,
  kis: m.kis ?? null,
  parc: m.parc ?? null,
  alp: m.alp ?? null,
  fixed_th: m.fixed_th ?? null,
  fixed_kis: m.fixed_kis ?? null,
  columns: m.columns ?? {},
  sheet: m.sheet ?? null,
  header_row: m.header_row ?? 1,
  paz: m.paz ?? null,
  sid: m.sid ?? null,
  filter_kis: null,
  filter_parc_from: null,
  filter_parc_to: null,
});

/** Kísérletek → Importálás: files of the lab instruments and spreadsheets into the measurement tables. The file is
 *  uploaded, its format recognised, the columns shown (and chosen, for a generic sheet), then the preview (a dry run)
 *  says what loading would do; loading runs as a background job, one undoable operation. */
function ImportPage() {
  const { t } = useTranslation();
  const canImport = hasRole(useUserMe().data?.roles, "nemesito");
  const ev = useSeasonFromLink(Route.useSearch().ev);
  const client = useQueryClient();
  const settings = useSettings();
  const saveSetting = useSaveSetting();
  const formats = useImportsFormats({ query: { enabled: canImport, staleTime: Number.POSITIVE_INFINITY } }).data ?? [];
  const tables = useMeasurementsTables({ query: { staleTime: Number.POSITIVE_INFINITY } }).data ?? [];
  const lineTables = useImportsLineTables({ query: { enabled: canImport, staleTime: Number.POSITIVE_INFINITY } }).data ?? [];
  const [uploads, setUploads] = useState<UploadInfo[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [mapping, setMapping] = useState<ImportMappingInput>(NO_MAPPING);
  const [policy, setPolicy] = useState<Policy>("skip");
  // the lines' values of a plot file (SKCS hardness → Pheno): legacy "Overwrite in Pheno", Yes by default
  const [linePolicy, setLinePolicy] = useState<Policy>("overwrite");
  const [alpZero, setAlpZero] = useState(false);
  const [accepted, setAccepted] = useState(false); // files an importer flagged (Farinograph-E ICC QN = HUN QN)
  const watched = useWatchedJob();
  const { jobId, job, watch } = watched;
  const reset = (files: UploadInfo[] = []) => {
    setUploads(files);
    setChosen(null);
    setMapping(NO_MAPPING);
    setAccepted(false);
    watch(null);
  };
  const upload = useImportsUpload({ mutation: { onSuccess: (files) => reset(files) } });

  const detected = uploads[0]?.format ?? null;
  const format = formats.find((f) => f.id === (chosen ?? detected));
  const kind = format?.mapping ?? null;
  // the preview classifies independently of the policy: it is asked again only when the file or the mapping changes
  const options = { alp_zero: alpZero, accept_warnings: accepted };
  const body: Omit<ImportIn, "policy"> | null =
    ev && format && uploads.length
      ? { ev, format: format.id, uploads: uploads.map((u) => u.id), mapping: kind ? mapping : null, options }
      : null;
  const lineRows = lineTables.some((x) => x.name === mapping.table) && kind === "generic";
  const registerRows = tables.some((x) => x.register && x.name === mapping.table) && kind === "generic";
  const preview = usePreview("import-preview", body, (b, signal) => importsPreview(b as ImportIn, { signal }));
  const result = preview.data;
  const header = result?.columns.map((c) => c.column) ?? [];

  // a generic sheet with a header seen before: its remembered mapping
  const template = result?.template ?? null;
  useEffect(() => {
    const kept = template ? settings?.import_templates[template] : undefined;
    if (kept && !mapping.table) setMapping({ ...NO_MAPPING, ...kept });
  }, [template, settings, mapping.table]);

  const start = useImportsStart({
    mutation: {
      onSuccess: (out) => {
        watch(out.job_id);
        if (template && kind && mapping.table && settings) {
          const kept = Object.entries(settings.import_templates)
            .filter(([k]) => k !== template)
            .slice(-29);
          saveSetting("import_templates", Object.fromEntries([...kept, [template, asTemplate(mapping)]]));
        }
      },
    },
  });
  useEffect(() => {
    if (job?.status === "done") {
      notifications.show({ color: "green", message: t("dataImport.loadDone", { count: job.result?.written ?? 0 }) });
      void client.invalidateQueries({ queryKey: ["measurements"] });
    } else if (job?.status === "failed") notifications.show({ color: "red", message: t("dataImport.loadFailed") });
  }, [job?.status, job?.result, client, t]);

  if (!canImport) {
    return (
      <div className="page">
        <PageHeader title={t("dataImport.title")} />
        <p className="mu">{t("shell.breederOnly")}</p>
      </div>
    );
  }

  const step = jobId ? 4 : result ? 3 : uploads.length ? 2 : 1;
  const count = result ? loadable(result, policy) : 0;
  const lineCount = result ? loadableLines(result, linePolicy) : 0;
  const files = (list: File[]) => {
    if (list.length) upload.mutate({ data: { files: list } });
  };
  return (
    <div className="page fill">
      <PageHeader
        title={t("dataImport.title")}
        season
        subtitle={t("dataImport.subtitle")}
        actions={<Steps names={STEPS.map((n) => t(`dataImport.steps.${n}`))} step={step} />}
      />
      <div className="hstack grow" style={{ gap: 16, alignItems: "stretch", minHeight: 0 }}>
        <FormatList formats={formats} value={format?.id ?? null} onChange={(id) => setChosen(id === detected ? null : id)} />
        <div className="stack grow" style={{ gap: 14, minWidth: 0, overflowY: "auto" }}>
          {uploads.length === 0 ? (
            <DropZone
              onFiles={files}
              busy={upload.isPending}
              multiple
              title={t("dataImport.dropFiles")}
              hint={t("dataImport.filesHelp")}
              button={t("dataImport.chooseFiles")}
            />
          ) : (
            <FileCard
              uploads={uploads}
              result={result}
              format={format}
              detected={!chosen && !!detected}
              action={
                <FileButton multiple onChange={files}>
                  {(props) => (
                    <Button size="xs" variant="default" style={{ flexShrink: 0 }} loading={upload.isPending} {...props}>
                      {t("dataImport.otherFile")}
                    </Button>
                  )}
                </FileButton>
              }
            />
          )}
          {kind && result && (
            <MappingControls
              kind={kind}
              mapping={mapping}
              header={header}
              sheets={result.sheet_names}
              tables={tables}
              lineTables={lineTables}
              trials={result.trials}
              onChange={(patch) => setMapping((m) => ({ ...m, ...patch }))}
            />
          )}
          {format?.id === "skcs" && (
            <Checkbox
              size="xs"
              label={t("dataImport.skcsAlpZero")}
              checked={alpZero}
              onChange={(e) => setAlpZero(e.currentTarget.checked)}
            />
          )}
          {preview.busy && !result && <Loader size="sm" />}
          {preview.error && <p className="note err sm">{String((preview.error as Error).message)}</p>}
          {result && (
            <ColumnTable
              columns={result.columns}
              mapping={kind === "generic" ? mapping : null}
              table={[...tables, ...lineTables].find((x) => x.name === mapping.table)}
              onTarget={(column, target) =>
                setMapping((m) => {
                  const columns = { ...m.columns };
                  if (target) columns[column] = target;
                  else delete columns[column];
                  return { ...m, columns };
                })
              }
            />
          )}
          {result && !jobId && result.rows > 0 && (
            <CheckPanel
              result={result}
              policy={policy}
              onPolicy={setPolicy}
              linePolicy={linePolicy}
              onLinePolicy={setLinePolicy}
              lines={lineRows}
              accepted={accepted}
              onAccept={setAccepted}
            />
          )}
          {jobId && (
            <section className="card pad stack" style={{ gap: 12 }}>
              <WatchedJobProgress {...watched} />
              {job?.status === "done" && (
                <span className="grow">{t("dataImport.loadDone", { count: formatNumber(Number(job.result?.written ?? 0)) })}</span>
              )}
              <Link to="/system/jobs" search={{ job: jobId }}>
                {t("dataImport.jobs")}
              </Link>
              {!lineRows && (
                <Link to="/trials/data" search={{ ev, table: Object.keys(result?.tables ?? {})[0] }}>
                  {t("dataImport.openTrialData")}
                </Link>
              )}
              <Button size="xs" variant="default" onClick={() => reset()}>
                {t("dataImport.newImport")}
              </Button>
            </section>
          )}
          {result && !jobId && (
            <div className="hstack" style={{ gap: 8, justifyContent: "flex-end", flexShrink: 0 }}>
              <Button variant="default" onClick={() => reset()}>
                {t("dataImport.back")}
              </Button>
              <Button
                disabled={!(count || lineCount) || !body || preview.busy}
                loading={start.isPending}
                onClick={() => body && start.mutate({ data: { ...body, policy, line_policy: linePolicy } })}
              >
                {lineRows
                  ? t("dataImport.loadLines", { count: formatNumber(count) })
                  : t(registerRows ? "dataImport.loadRegister" : "dataImport.load", { count: formatNumber(count), ev })}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
